import { useMemo } from 'react';
import type { Report } from '@reports/domain/entities';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import { ReportsActions } from './types';

import { useReportsContext } from './useReportsContext';

/**
 * Hook to access the reports state and actions
 *
 * @returns {ReportsState} Reports state and actions
 * @example
 * const { state, actions } = useReportsState();
 * actions.selectReport(report);
 * actions.resetFilters();
 * actions.updateFilters(filters);
 * actions.setInitialized();
 */
export const useReportsState = () => {
  const { state, dispatch } = useReportsContext();

  const actions = useMemo(
    () => ({
      selectReport: (report: Report) => dispatch({ type: ReportsActions.SELECT_REPORT, payload: { report } }),
      resetFilters: () => dispatch({ type: ReportsActions.RESET_FILTERS }),
      updateFilters: (filters: BlockDataRequestDto) =>
        dispatch({ type: ReportsActions.UPDATE_FILTERS, payload: { filters } }),
      setInitialized: () => dispatch({ type: ReportsActions.SET_INITIALIZED }),
    }),
    [dispatch],
  );

  return { state, actions };
};
